import { WebsiteBuilderHeadingLevel } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderHeading";
import React, { useEffect, useRef } from "react";

export interface IUseElementViewTextEditorArgs {
  id: string;
  text: string;
  level?: WebsiteBuilderHeadingLevel;
  editingText: boolean;
  dispatchText: (prevText: string, nextText: string) => void;
  ref: React.MutableRefObject<HTMLElement>;
}

type TElementViewTextEditorCursor = "text" | null;
type TElementViewTextEditorHandleOnPaste = (e: React.ClipboardEvent<HTMLParagraphElement>) => void;

interface IUseElementViewTextEditor {
  contentEditable: boolean;
  cursor: TElementViewTextEditorCursor;
  handleOnPaste: TElementViewTextEditorHandleOnPaste;
}

export function useElementViewTextEditor(args: IUseElementViewTextEditorArgs): IUseElementViewTextEditor {
  useEffect(() => {
    if (args.ref.current) {
      args.ref.current.textContent = args.text;
    }
  }, [args.text, args.level]);

  useEffect(() => {
    if (!args.ref.current) {
      return;
    }

    if (args.editingText) {
      const range = document.createRange();
      range.selectNodeContents(args.ref.current);

      const selection = window.getSelection();
      selection?.addRange(range);
    }

    return () => {
      const selection = window.getSelection();
      selection?.removeAllRanges();
    };
  }, [args.editingText]);

  const editingStarted = useRef<boolean>(false);

  useEffect(() => {
    if (!args.ref.current) {
      return;
    }

    if (args.editingText) {
      editingStarted.current = true;
    } else if (!args.editingText && editingStarted.current) {
      editingStarted.current = false;

      const updatedText = args.ref.current.textContent;

      if (args.text === updatedText) {
        return;
      }

      args.dispatchText(args.text, updatedText);
    }
  }, [args.editingText, args.text]);

  const handleOnPaste: TElementViewTextEditorHandleOnPaste = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  return {
    cursor: args.editingText ? "text" : null,
    contentEditable: args.editingText,
    handleOnPaste,
  };
}
